import React, { useState } from "react";
import { CenteredSpinner } from "../Common/CenteredSpinner";
import { Container, Row, Table } from "reactstrap";
import KaAlert from "../Common/KaAlert";
import useDisplayMessage from "../Common/useDisplayMessage";
import FilterInput, { textSatisfiesFilter } from "../FilterInput";
import { CreateEditBranch } from "./CreateEditBranch";
import { modalMode } from "../Common/EntityModal";
import PermissionButton from "../UserAccess/PermissionButton";
import { usePermission } from "../UserAccess/usePermission";
import EditViewButton from "../UserAccess/EditViewButton";
import { Button } from "react-bootstrap";
import Confirm from "../Common/Confirm";
import useGetBranches, { BranchDto } from "../../requests/useGetBranches";
import useRemoveBranch from "../../requests/useRemoveBranch";
import formatPostalAddress from "../Common/PostalAddressDisplay";
import { StaticSiteFilterPill } from "../UserAccess/SiteScope/SiteFilterPill";
import { ActionsColumnHeader } from "../Common/ActionsColumn";

enum States {
  None,
  AddEditBranch,
}

type PopupState =
  { state: States.None; } |
  {
    state: States.AddEditBranch;
    branch: BranchDto | null;
  };

const alphabeticallyByName = (a: BranchDto, b: BranchDto) => a.name.localeCompare(b.name);

const BranchPage = () => {
  const displayMessage = useDisplayMessage();
  const [filterText, setFilterText] = useState('');
  const [popupState, setPopupState] = useState<PopupState>({ state: States.None });
  const [branchToRemove, setBranchToRemove] = useState<BranchDto | undefined>(undefined);
  const canModify = usePermission('otherEntitiesModify');

  const getBranches = useGetBranches();

  const containsFilterText = (branch: BranchDto) => {
    const satisfiesFilter = textSatisfiesFilter(filterText);
    return satisfiesFilter(branch.name) || satisfiesFilter(branch.number);
  };

  const filterTextChanged = (text: string) => setFilterText(text);

  const removeSuccess = () => {
    displayMessage.success(`Branch ${branchToRemove?.name} removed`);
    return getBranches.query();
  };

  const removeRequest = useRemoveBranch(branchToRemove?.id ?? '', {
    onSuccess: removeSuccess,
    onError: (err) => displayMessage.fail(err.message),
    onComplete: () => setBranchToRemove(undefined),
  });

  const createClicked = () => {
    displayMessage.clear();
    setPopupState({ state: States.AddEditBranch, branch: null });
  }

  const editClicked = (branch: BranchDto) => {
    displayMessage.clear();
    setPopupState({ state: States.AddEditBranch, branch: branch });
  }

  const branchUpserted = (branchName: string) => {
    if (popupState.state !== States.AddEditBranch) return;
    displayMessage.success(`Branch ${branchName} ${popupState.branch ? 'updated' : 'created'}`);
    setPopupState({ state: States.None });
    getBranches.query();
  }

  const editDismissed = () => setPopupState({ state: States.None });

  const formatBranchPostalAddress = (branch: BranchDto) =>
    formatPostalAddress(
      branch.addressStreet,
      branch.addressCity,
      branch.addressState,
      branch.addressPostalCode,
      branch.addressCountry);

  const visibleBranches = (getBranches.data ?? [])
    .filter(containsFilterText)
    .sort(alphabeticallyByName);

  const loadingRows = getBranches.isLoading || removeRequest.isLoading;

  if (getBranches.isError) return <h3>{getBranches.error?.message}</h3>

  return <Container>
    {popupState.state === States.AddEditBranch && <CreateEditBranch
      initialBranch={popupState.branch}
      mode={modalMode(canModify, !popupState.branch)}
      onUpsert={branchUpserted}
      onDismiss={editDismissed} />}
    <Confirm
      visible={!!branchToRemove}
      title='Remove branch'
      body={`Are you sure that you want to remove branch ${branchToRemove?.name}?`}
      isLoading={removeRequest.isLoading}
      onDismiss={() => setBranchToRemove(undefined)}
      onConfirm={() => removeRequest.request()} />
    <Row className='justify-content-between mb-2'>
      <h2 className='col-auto ka-blue'>Branches</h2>
      <PermissionButton permission="otherEntitiesModify" className='col-auto btn-ghost-primary' onClick={createClicked}>Create Branch</PermissionButton>
    </Row>
    <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
    <form>
      <div className='row gx-2 gx-md-3 mb-3'>
        <div className='col-md-4 mb-2 mb-md-0'>
          <FilterInput placeholder='Search by name or number' onFilterTextChange={filterTextChanged} value={filterText} />
        </div>
        <div className='col-auto d-flex align-items-center'>
          <StaticSiteFilterPill entities="Branches" />
        </div>
      </div>
    </form>
    <Table bordered className="preserve-whitespace" style={{tableLayout: "fixed"}}>
      <thead>
        <tr>
          <th className='col-3'>Name</th>
          <th className='col-2'>Number</th>
          <th>Address</th>
          <ActionsColumnHeader />
        </tr>
      </thead>
      <tbody>
        {!loadingRows && visibleBranches.map(branch =>
          <tr key={branch.id}>
            <td>{branch.name}</td>
            <td>{branch.number}</td>
            <td>{formatBranchPostalAddress(branch)}</td>
            <td align="right" className='text-nowrap'>
              <div className='dropdown'>
                <Button id='actionsMenuButton'
                  className='dropdown-toggle btn-ghost-secondary'
                  variant='link'
                  data-bs-toggle='dropdown'
                  aria-expanded='false'><strong>Actions</strong></Button>
                <div className='dropdown-menu' aria-labelledby='actionsMenuButton'>
                  <EditViewButton
                    canModify={canModify}
                    className='dropdown-item btn-ghost-secondary'
                    onClick={() => editClicked(branch)} />
                  <PermissionButton permission="otherEntitiesModify"
                    className='dropdown-item btn-ghost-secondary'
                    onClick={() => setBranchToRemove(branch)}>Remove</PermissionButton>
                </div>
              </div>
            </td>
          </tr>)}
        {(loadingRows || visibleBranches.length === 0) &&
          <tr>
            <td colSpan={4} className='text-center text-muted py-3'>
              {loadingRows ? <CenteredSpinner /> : 'No branches to show'}
            </td>
          </tr>}
      </tbody>
    </Table>
  </Container>
};

export { BranchPage };
